import * as React from "react";
import { Linking, Pressable, ScrollView, Text, View } from "react-native";
import * as DocumentPicker from "expo-document-picker";
import * as ImagePicker from "expo-image-picker";
import {
  Link,
  router,
  Stack,
  useLocalSearchParams,
  usePathname,
  useRouter,
} from "expo-router";
import * as WebBrowser from "expo-web-browser";
import { Feather } from "@expo/vector-icons";
import { zodResolver } from "@hookform/resolvers/zod";
import AsyncStorage from "@react-native-async-storage/async-storage";
import Fuse from "fuse.js";
import { decodeEntity } from "html-entities";
import { observer } from "mobx-react-lite";
import { Controller, useForm } from "react-hook-form";
import * as DropdownMenu from "zeego/dropdown-menu";
import { z } from "zod";

import { getFileFormat } from "~/utils/getFileFormat";
import { useAccountActions } from "~/components/AccountActions/context";
import { showConfirmation } from "~/components/confirmation/showConfirmation";
import {
  StackHeaderBack,
  StackHeaderRight,
  StackHeaderTitle,
} from "~/components/HeaderElements";
import { Image } from "~/components/Image";
import { ResponsiveRenderer } from "~/components/ResponsiveRenderer";
import { getTiptapHTML } from "~/components/SaveForm/AddNote";
import { SelectablePill } from "~/components/SelectablePill";
import { backlogSlug } from "~/app/(app)/(authenticated)/[account]/(account)/backlog";
import Logo from "~/assets/logo.svg";
import { useCurrentAccount } from "~/hooks/useCurrentAccount";
import { useCurrentSpace } from "~/hooks/useCurrentSpace";
import { useGetR2AssetURL } from "~/hooks/useGetR2AssetURL";
import { icons, isWeb, useHackyParams } from "~/lib/constants";
import { cn, formatBytes } from "~/lib/utils";
import { useAccountStore } from "~/stores/space";
import { SaveFile, SaveMedia, SaveNote, SaveUrl } from "~/stores/space/store";
import { Button } from "~/ui/Button";
import { Dropdown } from "~/ui/Dropdown";
import { ListContainer, ListContainerTitleText, ListItem } from "~/ui/List";
import { TextInput } from "~/ui/TextInput";

type Params = {
  isSelecting: "true" | "false";
  ids: Array<string>;
  space: string;
  collectionId: string;
  tagId: string;
  sort: "date-asc" | "date-desc" | "title-asc" | "title-desc";
  filters?: string;
  search?: string;
};

function setParams(params: Partial<Params>) {
  router.setParams<Partial<Params>>(params);
}

function useParams() {
  return useLocalSearchParams<Params>();
}

const SAVE_TYPE_OPTIONS = [
  {
    type: "URL",
    title: "Link",
    icon: icons.link.web,
  },
  {
    type: "MEDIA",
    title: "Media",
    icon: icons.media.web,
  },
  {
    type: "FILE",
    title: "File",
    icon: icons.file.web,
  },
  {
    type: "NOTE",
    title: "Note",
    icon: icons.note.web,
  },
] as const;

type ViewType = "grid" | "list";

function AllSaves() {
  const params = useParams();
  const { sort = "date-desc", filters = "", search = "" } = params;

  const { space } = useCurrentSpace();
  const { replace } = useRouter();

  const [view, setView] = React.useState<ViewType | null>(null);
  React.useEffect(() => {
    // TODO: this should come from a user preference storage instead
    AsyncStorage.getItem("view").then((value) => {
      if (value) {
        setView(value as ViewType);
      } else {
        setView("grid");
      }
    });
  }, []);

  function getTitle() {
    if (filters) {
      return "Filtered Saves";
    }

    return "All Saves";
  }

  const saves = React.useMemo(() => {
    let saves = space.savesAsArray;

    if (filters) {
      const decodedFilters = decodeFilterFromURL(filters);
      if (decodedFilters) {
        saves = filterSaves(saves, decodedFilters);
      }
    }
    if (search) {
      saves = searchSaves(saves, search);
    }
    if (sort) {
      saves = sortSaves(saves, sort);
    }
    return saves;
  }, [filters, search, sort, space.savesAsArray]);

  if (!view) return null;

  return (
    <>
      <ResponsiveRenderer
        webDesktop={null}
        default={
          <Stack.Screen
            options={{
              headerLeft: () => <StackHeaderBack />,

              headerTitle: () => (
                <StackHeaderTitle>{getTitle()}</StackHeaderTitle>
              ),
              headerRight: () => (
                <StackHeaderRight>
                  <HeaderMenu />
                </StackHeaderRight>
              ),
              headerBackTitleVisible: false,
            }}
          />
        }
      />

      <ResponsiveRenderer
        webDesktop={
          <View className="max-h-full grow web:grid web:grid-cols-[auto_1fr]">
            <SidebarWeb />
            <View className="max-h-full grow overflow-auto">
              <View className="web:bg-surface-primary border-divider-strong flex-row items-start border-b px-5 py-4 web:sticky web:top-0 web:z-10">
                <View className="gap-2">
                  <Text className="text-28 flex flex-row items-baseline gap-2 font-semibold text-white">
                    {getTitle()}
                  </Text>
                  <View className="flex-row items-center gap-2">
                    <Filters />
                    {(space.collections.size > 0 || space.tags.size > 0) && (
                      <React.Fragment>
                        <DropdownMenu.Root>
                          <DropdownMenu.Trigger asChild>
                            <Button
                              variant="outline"
                              size="small"
                              iconAfter={
                                <Feather
                                  size={18}
                                  name="filter"
                                  className="text-icon"
                                />
                              }
                            >
                              <Text className="text-text-primary">Filter</Text>
                            </Button>
                          </DropdownMenu.Trigger>
                          <DropdownMenu.Content>
                            <FilterButton />
                          </DropdownMenu.Content>
                        </DropdownMenu.Root>
                        {params.filters && (
                          <Link
                            href={{
                              pathname: `/(app)/(authenticated)/[account]/(space)/[space]/saves`,
                              params: {
                                ...params,
                                filters: "",
                              },
                            }}
                            asChild
                            replace
                          >
                            <Button variant="outline" size="small">
                              <Text className="text-text-primary">
                                Clear filters
                              </Text>
                            </Button>
                          </Link>
                        )}
                      </React.Fragment>
                    )}
                  </View>
                </View>

                <View className="ml-auto flex-row gap-2">
                  <View className="border-input-border flex-row gap-0 rounded-lg border">
                    <Button
                      variant="outline"
                      size="small"
                      className={cn(
                        "border-none",
                        view === "grid" ? "bg-button-secondary" : "",
                      )}
                      iconBefore={
                        <Feather
                          className="text-text-primary"
                          name="grid"
                          size={16}
                        />
                      }
                      onPress={() => {
                        setView("grid");
                        AsyncStorage.setItem("view", "grid");
                      }}
                    />

                    <Button
                      variant="outline"
                      size="small"
                      className={cn(
                        "border-none",
                        view === "list" ? "bg-button-secondary" : "",
                      )}
                      iconBefore={
                        <Feather
                          className="text-text-primary"
                          name="list"
                          size={16}
                        />
                      }
                      onPress={() => {
                        setView("list");
                        AsyncStorage.setItem("view", "list");
                      }}
                    />
                  </View>
                  <Dropdown
                    asChild
                    trigger={
                      <Button
                        variant="outline"
                        size="small"
                        iconAfter={
                          <Feather
                            size={18}
                            name={(() => {
                              if (sort === "date-asc") return "arrow-up";
                              if (sort === "date-desc") return "arrow-down";
                              if (sort === "title-asc") return "arrow-up";
                              if (sort === "title-desc") return "arrow-down";
                            })()}
                            className="text-icon"
                          />
                        }
                      >
                        <Text className="text-text-primary">
                          {(() => {
                            if (sort === "date-asc") return "By date";
                            if (sort === "date-desc") return "By date";
                            if (sort === "title-asc")
                              return "By title (a to z)";
                            if (sort === "title-desc")
                              return "By title (z to a)";
                          })()}
                        </Text>
                      </Button>
                    }
                    groups={[
                      {
                        title: "Date",
                        items: [
                          {
                            icon: {
                              ios: "arrow.down",
                              web: "arrow-down",
                            },
                            key: "Date Descending",
                            title: "By date",
                            onSelect() {
                              replace({
                                pathname: `/(app)/(authenticated)/[account]/(space)/[space]/saves`,
                                params: {
                                  ...params,
                                  sort: "date-desc",
                                },
                              });
                            },
                          },
                          {
                            icon: {
                              ios: "arrow.up",
                              web: "arrow-up",
                            },
                            key: "Date Ascending",
                            title: "By date",
                            onSelect() {
                              replace({
                                pathname: `/(app)/(authenticated)/[account]/(space)/[space]/saves`,
                                params: {
                                  ...params,
                                  sort: "date-asc",
                                },
                              });
                            },
                          },
                        ],
                      },

                      {
                        title: "title",
                        items: [
                          {
                            icon: {
                              ios: "arrow.down",
                              web: "arrow-down",
                            },
                            key: "title Descending",
                            title: "By title (z to a)",
                            onSelect() {
                              replace({
                                pathname: `/(app)/(authenticated)/[account]/(space)/[space]/saves`,
                                params: {
                                  ...params,
                                  sort: "title-desc",
                                },
                              });
                            },
                          },
                          {
                            icon: {
                              ios: "arrow.up",
                              web: "arrow-up",
                            },
                            key: "Title Ascending",
                            title: "By title (a to z)",
                            onSelect() {
                              replace({
                                pathname: `/(app)/(authenticated)/[account]/(space)/[space]/saves`,
                                params: {
                                  ...params,
                                  sort: "title-asc",
                                },
                              });
                            },
                          },
                        ],
                      },
                    ]}
                  />
                </View>
              </View>
              <View className="gap-2 py-4">
                <Text className="text-text-tertiary px-4 uppercase">
                  {saves.length} {saves.length === 1 ? "save" : "saves"}
                </Text>
                {view === "list" ? (
                  <View className="gap-0">
                    {saves?.map((save) => (
                      <React.Fragment key={save.id}>
                        <SaveCard save={save} view={view} />
                        <View className="bg-grey-dark-3 h-px" />
                      </React.Fragment>
                    ))}
                  </View>
                ) : (
                  <View className="grid gap-4 px-4 web:grid-cols-[repeat(auto-fill,minmax(275px,1fr))]">
                    {saves?.map((save) => (
                      <SaveCard save={save} key={save.id} view={view} />
                    ))}
                  </View>
                )}
              </View>
            </View>
          </View>
        }
        default={
          <ScrollView>
            <View className="relative gap-4">
              <View className="flex-row items-center justify-center gap-2 px-4">
                <View className="flex-grow">
                  <TextInput placeholder="Search your saves" />
                </View>

                <Pressable className="border-input-border-active h-full max-h-full w-12 items-center justify-center rounded-md border p-2">
                  <Feather
                    name="sliders"
                    size={18}
                    className="text-text-primary rotate-90"
                  />
                </Pressable>
              </View>

              <View className="gap-2">
                <Text className="text-text-tertiary px-4 uppercase">
                  {saves.length} {saves.length === 1 ? "save" : "saves"}
                </Text>
                <View className="gap-0">
                  {saves?.map((save) => (
                    <React.Fragment key={save.id}>
                      <SaveCard view={view} save={save} key={save.id} />
                      <View className="bg-grey-dark-3 h-px" />
                    </React.Fragment>
                  ))}
                </View>
              </View>
            </View>
          </ScrollView>
        }
      />
    </>
  );
}

function SaveCard({
  save,
  view,
}: {
  save: SaveUrl | SaveMedia | SaveFile | SaveNote;
  view: ViewType;
}) {
  const formattedDate = new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "numeric",
    year: "numeric",
  }).format(new Date(save.createdAt));

  const { store } = useAccountStore();
  const { space } = useCurrentSpace();
  const { ids = [] } = useParams();
  const isChecked = ids.includes(save.id);

  const toggleSelect = () => {
    setParams({
      ids: ids.includes(save.id)
        ? ids.filter((id) => id !== save.id)
        : Array.from(new Set(ids.concat(save.id))),
    });
  };

  const accountActions = useAccountActions();

  const { getR2AssetURL } = useGetR2AssetURL();

  const dropdown = (
    <Dropdown
      asChild
      trigger={
        <Button
          size="small"
          variant="outline"
          iconBefore={<Feather name="more-vertical" size={18} />}
        />
      }
      groups={[
        {
          items: [
            {
              icon: icons.edit,
              title: "Edit",
              key: "edit",

              onSelect: () => {
                console.log(JSON.stringify(save, null, 2));
                accountActions.openEditSave({
                  ...save,
                  spaceId: space.id,
                });
              },
            },
            {
              icon: icons.copy,
              title: "Copy to",
              key: "copy",
              onSelect: () => {
                accountActions.openCopySave({
                  save,
                  srcSpaceId: space.id,
                });
              },
            },
            {
              icon: icons.delete,
              title: "Delete",
              key: "delete",
              destructive: true,
              onSelect: () => {
                showConfirmation({
                  onConfirm: () => {
                    store.deleteSave({
                      saveId: save.id,
                      spaceId: space.id,
                    });
                  },
                  onCancel: () => {
                    console.log("cancel delete save");
                  },
                });
              },
            },
          ],
        },
      ]}
    />
  );

  const getMetadata = () => {
    switch (save.type) {
      case "URL":
        return (
          <Text className="text-text-tertiary text-sm">
            <Feather
              name={icons.link.web}
              size={14}
              className="text-text-secondary"
            />{" "}
            {decodeEntity("&middot;")} {formattedDate}{" "}
            {decodeEntity("&middot;")} {new URL(save.url).hostname}
          </Text>
        );
      case "MEDIA":
        return (
          <Text className="text-text-tertiary text-sm">
            <Feather
              name={icons.media.web}
              size={14}
              className="text-text-secondary"
            />{" "}
            {decodeEntity("&middot;")} {formattedDate}{" "}
            {decodeEntity("&middot;")} {formatBytes(save.meta?.fileSize ?? 0)}{" "}
            {decodeEntity("&middot;")} {save.meta?.type}
          </Text>
        );
      case "FILE":
        return (
          <Text className="text-text-tertiary text-sm">
            <Feather
              name={icons.file.web}
              size={14}
              className="text-text-secondary"
            />{" "}
            {decodeEntity("&middot;")} {formattedDate}{" "}
            {decodeEntity("&middot;")} {formatBytes(save.meta?.size ?? 0)}{" "}
            {decodeEntity("&middot;")} {getFileFormat(save.meta?.mimeType)}
          </Text>
        );
      case "NOTE":
        return (
          <Text className="text-text-tertiary text-sm">
            <Feather
              name={icons.note.web}
              size={14}
              className="text-text-secondary"
            />{" "}
            {decodeEntity("&middot;")} {formattedDate}{" "}
          </Text>
        );
    }
  };

  const title = (
    <Text
      className="text-text-primary text-base web:text-ellipsis"
      numberOfLines={1}
      ellipsizeMode="tail"
    >
      {(() => {
        switch (save.type) {
          case "URL":
            return save.title || save.meta?.title;
          case "MEDIA":
            return save.title || save.meta?.fileName;
          case "FILE":
            return save.title || save.meta?.name;
          case "NOTE":
            return save.title || save.description;
        }
      })() || decodeEntity("&nbsp;")}
    </Text>
  );

  const onPress = () => {
    switch (save.type) {
      case "URL":
        return WebBrowser.openBrowserAsync(save.url, {
          presentationStyle: WebBrowser.WebBrowserPresentationStyle.POPOVER,
          enableBarCollapsing: true,
        });
      case "MEDIA":
        return window.open(getR2AssetURL(save.r2Key), "_blank");
      case "FILE":
        if (isWeb) {
          return window.open(getR2AssetURL(save.r2Key), "_blank");
        }
        // @ts-expect-error ...
        return Linking.openURL(save.meta?.uri);
      case "NOTE":
        accountActions.openEditSave({
          spaceId: space.id,
          ...save,
          step: "takeNote",
        });
    }
  };

  const getImageProps = () => {
    const getAltText = () => {
      switch (save.type) {
        case "URL":
          return save.title || save.meta?.title;
        case "MEDIA":
          return save.title || save.meta?.fileName;
        case "FILE":
          return save.title || save.meta?.name;
      }
    };
    return {
      source: (() => {
        switch (save.type) {
          case "URL":
            return save.meta?.image ?? save.meta?.logo;
          case "FILE":
          case "MEDIA":
            return getR2AssetURL(save.r2Key);
          case "NOTE":
            return "";
        }
      })(),

      alt: getAltText() ?? "undefined",
      "aria-label": getAltText() ?? undefined,
      placeholder: {
        uri: "thumbhash:/1QcSHQRnh493V4dIh4eXh1h4kJUI",
      },
    } as const;
  };

  const editButton = (
    <Button
      size="small"
      variant="outline"
      onPress={() => {
        accountActions.openEditSave({
          ...save,
          spaceId: space.id,
        });
      }}
      iconBefore={<Feather name={icons.edit.web} size={16} />}
    />
  );

  if (view === "list") {
    return (
      <>
        <View className="group flex-row items-center gap-4">
          <Pressable
            className={cn(
              "grow flex-row items-start gap-4 px-4 py-3",
              isChecked && "bg-grey-dark-5",
            )}
            onLongPress={() => {
              toggleSelect();
            }}
            onPress={onPress}
          >
            {(() => {
              if (save.type === "MEDIA" || save.type === "URL") {
                return (
                  <Image
                    {...getImageProps()}
                    style={{
                      width: 80,
                      height: 60,
                      borderRadius: 8,
                    }}
                  />
                );
              }

              if (save.type === "FILE") {
                return (
                  <View
                    className="items-center justify-center"
                    style={{
                      width: 80,
                      height: 60,
                    }}
                  >
                    <Feather
                      name={icons.file.web}
                      size={24}
                      className="text-text-primary"
                    />
                  </View>
                );
              }
            })()}

            <View className="flex-1 gap-1">
              {title}
              {getMetadata()}
            </View>
          </Pressable>
          <View className="ml-auto hidden flex-row gap-1 self-center px-4 group-hover:flex group-has-[[aria-expanded=true]]:flex">
            {editButton}
            {dropdown}
          </View>
        </View>
      </>
    );
  }

  return (
    <Pressable onPress={onPress}>
      <View className="bg-surface-secondary group relative grid h-full grid-cols-1 grid-rows-[145px_auto] overflow-hidden rounded">
        {(() => {
          if (save.type === "MEDIA" || save.type === "URL") {
            return (
              <Image
                {...getImageProps()}
                style={{
                  height: 145,
                  borderRadius: 8,
                }}
              />
            );
          }

          if (save.type === "FILE") {
            return (
              <View className="items-center justify-center">
                <Feather
                  name={icons.file.web}
                  size={28}
                  className="text-text-primary"
                />
              </View>
            );
          }

          if (save.type === "NOTE") {
            return (
              <View className="items-center justify-center p-2">
                {isWeb ? (
                  <div
                    className="tiptap overflow-hidden"
                    dangerouslySetInnerHTML={{
                      __html: (() => {
                        try {
                          return getTiptapHTML(JSON.parse(save.richTextAST));
                        } catch {
                          return "";
                        }
                      })(),
                    }}
                  />
                ) : null}
              </View>
            );
          }
        })()}

        <View className="border-surface-tertiary h-full flex-1 gap-1 border-t px-3 py-4">
          <View className="h-9 flex-row gap-2">
            {title}
            <View className="ml-auto hidden flex-row gap-1 group-hover:flex group-has-[[aria-expanded=true]]:flex">
              {editButton}
              {dropdown}
            </View>
          </View>
          {getMetadata()}
        </View>
      </View>
    </Pressable>
  );
}

export const encodeSaveIDsForURL = (ids: Array<string>): string =>
  ids.map((id) => `${encodeURIComponent(id)}`).join("&");

export const decodeSaveIDsFromURL = (ids: string): Array<string> =>
  ids.split("&").map((id) => decodeURIComponent(id));

function HeaderMenu() {
  const { push } = useRouter();
  const { slug } = useCurrentAccount();
  const { space } = useCurrentSpace();

  return (
    <>
      <Dropdown
        trigger={
          <Text className="text-white">
            <Feather name="more-horizontal" size={24} />
          </Text>
        }
        groups={[
          {
            title: "select",
            items: [
              {
                icon: icons.select,
                title: "Select",
                key: "select",
                onSelect: () => {
                  setParams({ isSelecting: "true" });
                },
              },
            ],
          },
          {
            title: "add",
            items: [
              {
                key: "media",
                title: "Add Media",
                icon: icons.media,
                async onSelect() {
                  try {
                    await ImagePicker.requestMediaLibraryPermissionsAsync();
                    const result = await ImagePicker.launchImageLibraryAsync({
                      mediaTypes: ImagePicker.MediaTypeOptions.All,
                      presentationStyle:
                        ImagePicker.UIImagePickerPresentationStyle.FORM_SHEET,
                    });

                    if (!result.canceled) {
                      push({
                        pathname: "/(app)/(authenticated)/[account]/link/",
                        params: {
                          account: slug,
                          type: "MEDIA",
                          asset: encodeURIComponent(
                            JSON.stringify(result.assets[0]),
                          ),
                          spaceId: space.id,
                        },
                      });
                    }
                  } catch (e) {
                    console.error(e);
                  }
                },
              },
              {
                key: "file",
                title: "Add File",
                icon: icons.file,

                async onSelect() {
                  try {
                    const result = await DocumentPicker.getDocumentAsync();

                    if (!result.canceled) {
                      push({
                        pathname: "/(app)/(authenticated)/[account]/link/",
                        params: {
                          account: slug,
                          type: "FILE",
                          asset: encodeURIComponent(
                            JSON.stringify(result.assets[0]),
                          ),
                          spaceId: space.id,
                        },
                      });
                    }
                  } catch (e) {
                    console.error(e);
                  }
                },
              },
              {
                key: "link",
                title: "Add Link",
                icon: icons.link,
                onSelect() {},
              },
            ],
          },
          // {
          //   items: [
          //     {
          //       icon: {
          //         ios: "grid",
          //         web: "grid",
          //       },
          //       title: "Grid view",
          //       key: "grid-view",
          //       onSelect: () => {
          //         setParams({ view: "grid" });
          //       },
          //     },

          //     {
          //       icon: {
          //         ios: "list.bullet",
          //         web: "list",
          //       },
          //       title: "List view",
          //       key: "list-view",
          //       onSelect: () => {
          //         setParams({ view: "list" });
          //       },
          //     },
          //   ],
          // },
        ]}
      />
    </>
  );
}

export default observer(AllSaves);

function SidebarMenuWeb() {
  const { space } = useCurrentSpace();
  const { store } = useAccountStore();
  const account = useCurrentAccount();
  const accountActions = useAccountActions();
  const { replace } = useRouter();
  return (
    <>
      <Dropdown
        asChild
        trigger={
          <Button
            variant="outline"
            size={"small"}
            iconBefore={<Feather name="more-horizontal" size={18} />}
            className="invisible group-hover/sidebar:visible group-has-[[aria-expanded=true]]/sidebar:visible"
          />
        }
        groups={[
          {
            items: (
              [
                {
                  key: "Edit",
                  title: "Edit workspace",
                  icon: icons.edit,
                  onSelect: () => {
                    accountActions.openEditSpace({
                      spaceId: space.id,
                    });
                  },
                },

                {
                  key: "newCollection",
                  title: "New collection",
                  icon: icons.collection,
                  onSelect: () => {
                    accountActions.openAddCollection({
                      spaceId: space.id,
                    });
                  },
                },

                {
                  key: "newTag",
                  title: "New tag",
                  icon: icons.tag,
                  onSelect: () => {
                    accountActions.openAddTag({
                      spaceId: space.id,
                    });
                  },
                },

                // {
                //   key: "Share",
                //   title: "Share",
                //   icon: {
                //     ios: "square.and.arrow.up",
                //     web: "share",
                //   },
                // },
                {
                  key: "Delete",
                  title: "Delete",
                  icon: icons.delete,
                  onSelect: () => {
                    showConfirmation({
                      onConfirm: () => {
                        store.deleteSpace({
                          spaceId: space.id,
                        });
                        replace({
                          pathname:
                            "/(app)/(authenticated)/[account]/(account)/",
                          params: {
                            account: account.slug,
                          },
                        });
                      },
                    });
                  },
                  destructive: true,
                },
              ] as const
            ).filter((item) => {
              if (space.slug === backlogSlug && item.key === "newCollection")
                return false;
              return true;
            }),
          },
        ]}
      />
    </>
  );
}

const filterSchema = z.object({
  collection: z.object({
    ids: z.array(z.string()).default([]),
    mode: z
      .enum(["is", "is_not", "is_any_of", "is_none_of", "is_all_of"])
      .default("is"),
  }),

  tag: z.object({
    ids: z.array(z.string()).default([]),
    mode: z
      .enum(["is", "is_not", "is_any_of", "is_none_of", "is_all_of"])
      .default("is"),
  }),

  saveType: z
    .object({
      // calling ID to make code easier
      ids: z.array(z.enum(["URL", "MEDIA", "FILE", "NOTE"])).default([]),
      mode: z.enum(["is", "is_not", "is_any_of", "is_none_of"]).default("is"),
    })
    .optional(),
});

type filterSchemaType = z.infer<typeof filterSchema>;
function FilterButton() {
  const params = useHackyParams<{ filters: string }>();
  const { handleSubmit, control } = useForm<filterSchemaType>({
    resolver: zodResolver(filterSchema),
    defaultValues: decodeFilterFromURL(params.filters) ?? {
      collection: {
        ids: [],
      },
      tag: {
        ids: [],
      },
    },
  });
  const space = useCurrentSpace();
  const pathname = usePathname();
  const { replace } = useRouter();

  // this is mostly useful when switch from singular filter type to multiple filter types
  function getDefaultMode(
    x:
      | filterSchemaType["collection"]
      | filterSchemaType["tag"]
      | filterSchemaType["saveType"],
  ) {
    if (!x) return "is";
    if (x.ids.length > 1) {
      if (["is_any_of", "is_all_of", "is_none_of"].includes(x.mode)) {
        return x.mode;
      }
      return "is_any_of";
    }

    if (["is", "is_not"].includes(x.mode)) {
      return x.mode;
    }

    return "is";
  }

  const toRender: Array<React.ReactElement> = [];

  toRender.push(
    <View className="gap-2" key="saveType">
      <View className="flex-row items-baseline gap-1">
        <Text className="text-text-primary uppercase">Type</Text>
      </View>
      <View className="flex-row flex-wrap gap-2">
        <Controller
          control={control}
          name="saveType.ids"
          render={({ field }) => {
            return (
              <>
                {SAVE_TYPE_OPTIONS.map((s) => {
                  const isChecked = field.value?.includes(s.type);
                  return (
                    <SelectablePill
                      key={s.type}
                      onPress={() => {
                        const types = Array.isArray(field.value)
                          ? [...field.value]
                          : [];
                        const updatedTags = isChecked
                          ? types.filter((type) => type !== s.type)
                          : [...types, s.type];
                        field.onChange(updatedTags);
                      }}
                      isChecked={isChecked}
                      iconBefore={{ web: s.icon }}
                    >
                      {s.title}
                    </SelectablePill>
                  );
                })}
              </>
            );
          }}
        />
      </View>
    </View>,
  );

  if (space.space.collections.size > 0) {
    toRender.push(
      <View className="gap-2" key="collections">
        <View className="flex-row items-baseline gap-1">
          <Text className="text-text-primary uppercase">Collections</Text>
          <Feather name={icons.collection.web} className="text-text-primary" />
        </View>
        <View className="flex-row flex-wrap gap-2">
          <Controller
            control={control}
            name="collection.ids"
            render={({ field }) => {
              return (
                <>
                  {space.space.collectionsAsArray.map((collection) => {
                    const isChecked = field.value.includes(collection.id);
                    return (
                      <SelectablePill
                        key={collection.id}
                        onPress={() => {
                          const collections = Array.isArray(field.value)
                            ? [...field.value]
                            : [];
                          const updatedTags = isChecked
                            ? collections.filter(
                                (collectionId) =>
                                  collectionId !== collection.id,
                              )
                            : [...collections, collection.id];
                          field.onChange(updatedTags);
                        }}
                        isChecked={isChecked}
                      >
                        {collection.title ?? collection.slug}
                      </SelectablePill>
                    );
                  })}
                </>
              );
            }}
          />
        </View>
      </View>,
    );
  }

  if (space.space.tags.size > 0) {
    toRender.push(
      <View key="tags" className="gap-2">
        <View className="flex-row items-baseline gap-1">
          <Text className="text-text-primary uppercase">Tags</Text>
          <Feather name={icons.tag.web} className="text-text-primary" />
        </View>
        <View className="flex-row flex-wrap gap-2">
          <Controller
            control={control}
            name="tag.ids"
            render={({ field }) => (
              <>
                {space.space.tagsAsArray.map((tag) => {
                  const isChecked = field.value.includes(tag.id);
                  return (
                    <SelectablePill
                      isChecked={isChecked}
                      key={tag.id}
                      onPress={() => {
                        const tags = Array.isArray(field.value)
                          ? [...field.value]
                          : [];
                        const updatedTags = isChecked
                          ? tags.filter((tagId) => tagId !== tag.id)
                          : [...tags, tag.id];
                        field.onChange(updatedTags);
                      }}
                    >
                      {tag.value}
                    </SelectablePill>
                  );
                })}
              </>
            )}
          />
        </View>
      </View>,
    );
  }

  return (
    <View className="bg-surface-primary max-w-96 gap-4 rounded p-2">
      {toRender.map((x, i) => {
        if (i === toRender.length - 1) return x;
        return (
          <React.Fragment key={i}>
            {x}
            <View className="bg-grey-dark-3 h-px" />
          </React.Fragment>
        );
      })}

      <Button
        variant="primary"
        size="small"
        onPress={handleSubmit((data) => {
          replace({
            pathname: pathname,
            params: {
              filters: encodeFilterForURL({
                collection: {
                  ids: data.collection.ids,
                  mode: getDefaultMode(data.collection),
                },
                tag: {
                  ids: data.tag.ids,
                  mode: getDefaultMode(data.tag),
                },
                saveType: data.saveType
                  ? {
                      ids: data.saveType.ids,

                      mode: getDefaultMode(data.saveType) as Exclude<
                        filterSchemaType["saveType"],
                        undefined
                      >["mode"],
                    }
                  : undefined,
              }),
            },
          });
        })}
      >
        Apply filter
      </Button>
      <Button
        variant="outline"
        size="small"
        onPress={() => {
          replace({
            pathname: pathname,
          });
        }}
      >
        Clear filter
      </Button>
    </View>
  );
}

function Filters() {
  const params = useHackyParams<{ filters: string }>();
  const filters = decodeFilterFromURL(params.filters);
  const { space } = useCurrentSpace();
  const toRender: Array<React.ReactElement> = [];
  const pathname = usePathname();
  const { push } = useRouter();

  const filterOptions: Array<{
    key: filterSchemaType["collection"]["mode"];
    title: string;
  }> = [
    {
      key: "is",
      title: "is",
    },
    {
      key: "is_not",
      title: "is not",
    },

    {
      key: "is_any_of",
      title: "is any of",
    },
    {
      key: "is_all_of",
      title: "is all of",
    },
    {
      key: "is_none_of",
      title: "is none of",
    },
  ];

  if (!filters) {
    return null;
  }

  if (
    filters.collection.ids.length === 0 &&
    filters.tag.ids.length === 0 &&
    !filters.saveType
  ) {
    return null;
  }

  function getFilterOptions(type: keyof filterSchemaType) {
    if (!type) return [];
    if (!filters) return [];

    const length = filters[type]?.ids.length ?? 0;

    if (length === 1) {
      return filterOptions
        .filter((option) => option.key === "is" || option.key === "is_not")
        .map((x) => ({
          ...x,
          onSelect() {
            push({
              pathname: pathname,
              params: {
                filters: encodeFilterForURL({
                  ...filters,
                  [type]: {
                    ...filters[type],
                    mode: x.key,
                  },
                }),
              },
            });
          },
        }));
    }

    return filterOptions
      .filter((option) => {
        if (type === "saveType")
          return option.key === "is_any_of" || option.key === "is_none_of";

        return (
          option.key === "is_any_of" ||
          option.key === "is_none_of" ||
          option.key === "is_all_of"
        );
      })
      .map((x) => ({
        ...x,
        onSelect() {
          push({
            pathname: pathname,
            params: {
              filters: encodeFilterForURL({
                ...filters,
                [type]: {
                  ...filters[type],
                  mode: x.key,
                },
              }),
            },
          });
        },
      }));
  }

  if (filters.saveType && filters.saveType?.ids.length > 0) {
    toRender.push(
      <View key="saveType" className="flex-row items-center gap-1">
        <Feather
          className="text-text-primary"
          name={icons.file.web}
          size={16}
        />
        <Text className="text-text-primary">Type</Text>

        <Dropdown
          asChild
          trigger={
            <Button variant="outline" size="small">
              <Text className="text-text-primary">
                {filters.saveType.mode.replaceAll("_", " ")}
              </Text>
            </Button>
          }
          groups={[
            {
              items: getFilterOptions("saveType"),
            },
          ]}
        />
        {(() => {
          return (
            <Text className="text-text-primary">
              {filters.saveType.ids
                .map((type) => {
                  return SAVE_TYPE_OPTIONS.find((x) => x.type === type)?.title;
                })
                .join(", ")}
            </Text>
          );
        })()}
      </View>,
    );
  }

  if (filters.collection.ids.length > 0) {
    const collectionIdsLength = filters.collection.ids.length;

    toRender.push(
      <View key="collection" className="flex-row items-center gap-1">
        <Feather
          className="text-text-primary"
          name={icons.collection.web}
          size={16}
        />
        <Text className="text-text-primary">Collection</Text>

        <Dropdown
          asChild
          trigger={
            <Button variant="outline" size="small">
              <Text className="text-text-primary">
                {filters.collection.mode.replaceAll("_", " ")}
              </Text>
            </Button>
          }
          groups={[
            {
              items: getFilterOptions("collection"),
            },
          ]}
        />
        {(() => {
          if (collectionIdsLength === 1) {
            return (
              <Text className="text-text-primary">
                {
                  space.collectionById(filters.collection.ids.at(0) ?? "")
                    ?.title
                }
              </Text>
            );
          }
          if (collectionIdsLength > 1) {
            return (
              <View className="flex-row items-center gap-1">
                <Text className="text-text-primary">
                  {
                    space.collectionById(filters.collection.ids.at(0) ?? "")
                      ?.title
                  }
                </Text>
                <Text className="text-text-primary">
                  +{collectionIdsLength - 1} more
                </Text>
              </View>
            );
          }
          return null;
        })()}
      </View>,
    );
  }

  if (filters.tag.ids.length > 0) {
    const tagIdsLength = filters.tag.ids.length;

    toRender.push(
      <View key="tag" className="flex-row items-center gap-1">
        <Feather className="text-text-primary" name={icons.tag.web} size={16} />
        <Text className="text-text-primary">Tag</Text>

        <Dropdown
          asChild
          trigger={
            <Button variant="outline" size="small">
              <Text className="text-text-primary">
                {filters.tag.mode.replaceAll("_", " ")}
              </Text>
            </Button>
          }
          groups={[
            {
              items: getFilterOptions("tag"),
            },
          ]}
        />
        {(() => {
          if (tagIdsLength === 1) {
            return (
              <Text className="text-text-primary">
                {space.tagById(filters.tag.ids.at(0) ?? "")?.value}
              </Text>
            );
          }
          if (tagIdsLength > 1) {
            return (
              <View className="flex-row items-center gap-1">
                <Text className="text-text-primary">
                  {space.tagById(filters.tag.ids.at(0) ?? "")?.value}
                </Text>
                <Text className="text-text-primary">
                  +{tagIdsLength - 1} more
                </Text>
              </View>
            );
          }
          return null;
        })()}
      </View>,
    );
  }

  return (
    <View className="flex-row items-center gap-2">
      {toRender.map((x, i) => {
        if (i === toRender.length - 1) return x;
        return (
          <React.Fragment key={`divider-${i}`}>
            {x}
            <Text className="text-text-secondary">{decodeEntity("&amp;")}</Text>
          </React.Fragment>
        );
      })}
    </View>
  );
}

export function encodeFilterForURL(filter: filterSchemaType) {
  return btoa(JSON.stringify(filter));
}

function decodeFilterFromURL(filter: string = "") {
  const parsed = filterSchema.safeParse(filter ? JSON.parse(atob(filter)) : "");
  if (parsed.success) {
    return parsed.data;
  }

  return null;
}

function filterSaves(
  saves: Array<SaveUrl | SaveMedia | SaveFile | SaveNote>,
  filters: filterSchemaType,
) {
  if (!filters) return saves;

  const filteredSaves = saves
    .filter((save) => {
      if (filters.collection.ids.length === 0) return true;
      switch (filters.collection.mode) {
        case "is":
          return save.collections.has(filters.collection.ids.at(0) ?? "");
        case "is_not":
          return !save.collections.has(filters.collection.ids.at(0) ?? "");
        case "is_all_of":
          return filters.collection.ids.every((collectionId) =>
            save.collections.has(collectionId),
          );
        case "is_any_of":
          return filters.collection.ids.some((collectionId) =>
            save.collections.has(collectionId),
          );
        case "is_none_of":
          return !filters.collection.ids.some((collectionId) =>
            save.collections.has(collectionId),
          );
        default:
          const _exhaustiveCheck: never = filters.collection.mode;
          return _exhaustiveCheck;
      }
    })
    .filter((save) => {
      if (filters.tag.ids.length === 0) return true;

      switch (filters.tag.mode) {
        case "is":
          return save.tags.has(filters.tag.ids.at(0) ?? "");
        case "is_not":
          return !save.tags.has(filters.tag.ids.at(0) ?? "");
        case "is_all_of":
          return filters.tag.ids.every((tagId) => save.tags.has(tagId));
        case "is_any_of":
          return filters.tag.ids.some((tagId) => save.tags.has(tagId));
        case "is_none_of":
          return !filters.tag.ids.some((tagId) => save.tags.has(tagId));
        default:
          const _exhaustiveCheck: never = filters.tag.mode;
          return _exhaustiveCheck;
      }
    })
    .filter((save) => {
      if (!filters.saveType) return true;
      if (filters.saveType?.ids.length === 0) return true;

      switch (filters.saveType.mode) {
        case "is":
          return filters.saveType.ids.includes(save.type);
        case "is_not":
          return !filters.saveType.ids.includes(save.type);
        case "is_any_of":
          return filters.saveType.ids.some((type) => save.type === type);
        case "is_none_of":
          return !filters.saveType.ids.some((type) => save.type === type);
        default:
          const _exhaustiveCheck: never = filters.saveType.mode;
          return _exhaustiveCheck;
      }
    });

  return filteredSaves;
}

function searchSaves(
  saves: Array<SaveUrl | SaveMedia | SaveFile | SaveNote>,
  search: string,
) {
  const fuse = new Fuse(saves, {
    keys: [
      "title",
      "description",
      "url",
      "meta.title",
      "meta.fileName",
      "meta.name",
      "richTextAsText",
    ],
    minMatchCharLength: 2,
  });
  return fuse.search(search).map((result) => result.item);
}

function sortSaves(
  saves: Array<SaveUrl | SaveMedia | SaveFile | SaveNote>,
  sort: "date-asc" | "date-desc" | "title-asc" | "title-desc",
) {
  return saves.toSorted((a, b) => {
    const titleA = a.title ?? "";
    const titleB = b.title ?? "";
    if (sort === "title-asc") {
      return titleA.localeCompare(titleB);
    }
    if (sort === "title-desc") {
      return titleB.localeCompare(titleA);
    }
    if (sort === "date-asc") {
      return a.createdAt > b.createdAt ? 1 : -1;
    }

    if (sort === "date-desc") {
      return a.createdAt < b.createdAt ? 1 : -1;
    }
    return 0;
  });
}

const SidebarWeb = observer(function SidebarWeb() {
  const params = useParams();
  const { collectionId, tagId } = params;

  const { space, account } = useCurrentSpace();

  const { store } = useAccountStore();
  const accountActions = useAccountActions();

  return (
    <View
      className="group/sidebar border-divider-strong overflow-auto border-r px-2 py-2"
      style={{
        width: 300,
      }}
    >
      <View className="flex-row justify-between px-2 py-5">
        <Text className="text-xl font-semibold text-white">
          {space.displayName}
        </Text>
        <SidebarMenuWeb />
      </View>

      <View className="gap-y-8">
        <ListContainer>
          <ListItem
            className={cn("border-r-4 border-transparent")}
            href={{
              pathname: `/(app)/(authenticated)/[account]/(space)/[space]/saves`,
              params: {
                account: account.slug,
                space: space.slug,
              },
            }}
          >
            <View className="flex-row items-center gap-2">
              <Logo width={18} height={18} />
              <Text className="text-base text-white">All Saves</Text>
            </View>
            <Text className="text-text-secondary text-base">
              {space.savesCount}
            </Text>
          </ListItem>
          {SAVE_TYPE_OPTIONS.map((type) => {
            const saveCount = space.savesAsArray.filter(
              (save) => save.type === type.type,
            ).length;

            return (
              <ListItem
                key={type.type}
                className={cn("border-r-4 border-transparent")}
                href={{
                  pathname: `/(app)/(authenticated)/[account]/(space)/[space]/saves`,
                  params: {
                    account: account.slug,
                    space: space.slug,
                    filters: encodeFilterForURL({
                      collection: {
                        ids: [],
                        mode: "is",
                      },
                      tag: {
                        ids: [],
                        mode: "is",
                      },
                      saveType: {
                        ids: [type.type],
                        mode: "is",
                      },
                    }),
                  },
                }}
              >
                <View className="flex-row items-center gap-2">
                  <Feather
                    name={type.icon}
                    size={18}
                    className="text-text-secondary"
                  />
                  <Text className="text-base text-white">{type.title}</Text>
                </View>
                <Text className="text-text-secondary text-base">
                  {saveCount}
                </Text>
              </ListItem>
            );
          })}
        </ListContainer>

        {space.collections.size > 0 && (
          <ListContainer
            className="group/container"
            title={
              <View className="flex-row items-center">
                <View className="flex-row gap-2">
                  <Feather
                    name={icons.collection.web}
                    size={18}
                    className="text-text-secondary"
                  />
                  <ListContainerTitleText>Collections</ListContainerTitleText>
                </View>

                <Pressable
                  onPress={() => {
                    accountActions.openAddCollection({
                      spaceId: space.id,
                    });
                  }}
                  className="bg-surface-secondary ml-auto rounded-sm p-1 opacity-0 group-hover/container:opacity-100"
                >
                  <Feather
                    name="plus"
                    size={18}
                    className="text-text-primary"
                  />
                </Pressable>
              </View>
            }
          >
            {space.collectionsAsArray.map((collection) => {
              const saveCount = space.savesAsArray.filter((save) =>
                save.collections.has(collection.id),
              ).length;

              return (
                <ListItem
                  className={cn(
                    "group gap-2 border-r-4 border-transparent",
                    collectionId === collection.id && "border-button-primary",
                  )}
                  key={collection.id}
                  href={{
                    pathname: `/(app)/(authenticated)/[account]/(space)/[space]/saves`,
                    params: {
                      account: account.slug,
                      space: space.slug,
                      filters: encodeFilterForURL({
                        collection: {
                          ids: [collection.id],
                          mode: "is",
                        },
                        tag: {
                          ids: [],
                          mode: "is",
                        },
                      }),
                    },
                  }}
                >
                  <View className="flex-row items-center">
                    <Text className="text-base text-white">
                      {collection.title}
                    </Text>
                  </View>
                  <Text className="text-text-secondary ml-auto inline-block text-base group-hover:hidden group-has-[[aria-expanded=true]]:hidden">
                    {saveCount}
                  </Text>
                  <>
                    <Dropdown
                      asChild
                      trigger={
                        <Pressable
                          role="button"
                          className="hidden group-hover:flex group-has-[[aria-expanded=true]]:flex"
                        >
                          <Text className="text-text-primary">
                            <Feather name="more-vertical" size={18} />
                          </Text>
                        </Pressable>
                      }
                      groups={[
                        {
                          items: [
                            {
                              icon: icons.edit,
                              title: "Edit collection",
                              key: "edit",
                              onSelect: () => {
                                accountActions.openEditCollection({
                                  collection,
                                  space,
                                });
                              },
                            },

                            {
                              icon: icons.delete,
                              title: "Delete collection",
                              key: "delete",
                              destructive: true,
                              onSelect: () => {
                                showConfirmation({
                                  onConfirm: () => {
                                    store.deleteCollection({
                                      spaceId: space.id,
                                      collectionId: collection.id,
                                    });
                                  },
                                });
                              },
                            },
                          ] as const,
                        },
                      ]}
                    />
                  </>
                </ListItem>
              );
            })}
          </ListContainer>
        )}

        {space.tags.size > 0 && (
          <ListContainer
            className="group/container"
            title={
              <View className="flex-row items-center">
                <View className="flex-row gap-2">
                  <Feather
                    name={icons.tag.web}
                    size={18}
                    className="text-text-secondary"
                  />
                  <ListContainerTitleText>Tags</ListContainerTitleText>
                </View>
                <Pressable
                  onPress={() => {
                    accountActions.openAddTag({
                      spaceId: space.id,
                    });
                  }}
                  className="bg-surface-secondary ml-auto rounded-sm p-1 opacity-0 group-hover/container:opacity-100"
                >
                  <Feather
                    name="plus"
                    size={18}
                    className="text-text-primary"
                  />
                </Pressable>
              </View>
            }
          >
            {space.tagsAsArray.map((tag) => {
              const saveCount = space.savesAsArray.filter((save) =>
                save.tags.has(tag.id),
              ).length;

              return (
                <ListItem
                  className={cn(
                    "group gap-2 border-r-4 border-transparent",
                    tagId === tag.id && "border-button-primary border-r-4",
                  )}
                  key={tag.id}
                  href={{
                    pathname: `/(app)/(authenticated)/[account]/(space)/[space]/saves`,
                    params: {
                      account: account.slug,
                      space: space.slug,
                      filters: encodeFilterForURL({
                        collection: {
                          ids: [],
                          mode: "is",
                        },
                        tag: {
                          ids: [tag.id],
                          mode: "is",
                        },
                      }),
                    },
                  }}
                >
                  <View className="flex-row items-center">
                    <Text className="text-base text-white">{tag.value}</Text>
                  </View>
                  <Text className="text-text-secondary ml-auto inline-block text-base group-hover:hidden group-has-[[aria-expanded=true]]:hidden">
                    {saveCount}
                  </Text>
                  <>
                    <Dropdown
                      asChild
                      trigger={
                        <Pressable
                          role="button"
                          className="hidden group-hover:flex group-has-[[aria-expanded=true]]:flex"
                        >
                          <Text className="text-text-primary">
                            <Feather name="more-vertical" size={18} />
                          </Text>
                        </Pressable>
                      }
                      groups={[
                        {
                          items: [
                            {
                              icon: icons.edit,
                              title: "Edit tag",
                              key: "edit",
                              onSelect: () => {
                                accountActions.openEditTag({
                                  tag: tag,
                                  space,
                                });
                              },
                            },

                            {
                              icon: icons.delete,
                              title: "Delete tag",
                              key: "delete",
                              destructive: true,
                              onSelect: () => {
                                showConfirmation({
                                  onConfirm: () => {
                                    store.deleteTag({
                                      spaceId: space.id,
                                      tagId: tag.id,
                                    });
                                  },
                                });
                              },
                            },
                          ] as const,
                        },
                      ]}
                    />
                  </>
                </ListItem>
              );
            })}
          </ListContainer>
        )}
      </View>
    </View>
  );
});
